import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useLocation, useNavigate } from 'react-router';
import {
  Typography,
  Skeleton,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TablePagination,
  Box,
  Menu,
  MenuItem,
  Chip,
  IconButton,
  TextField,
  InputAdornment,
  Autocomplete,
  CircularProgress,
  Tooltip,
} from '@mui/material';
import { KeyboardArrowDown, KeyboardArrowRight, Clear, FilterAlt } from '@mui/icons-material';

import useGetChangeTickets from '../hooks/vendors/useGetChangeTickets';
import usePaginatedOrgs from '../hooks/usePaginatedOrgs';
import VendorStatusBadge from './VendorStatusBadge';
import { StandardVendorNameCell, TicketActionsCell } from './VendorChangeTicketCells';
import { TVendorStatus, VendorChangeTicketEntry } from '../../types/vendor-types';
import { LinkTableStyled } from '../common/StyledComponents';
import { IOrg } from '../../types/my-types';
import { VENDOR_STATUS_CONFIG, DOC_REVIEWED_OPTIONS } from '../../constants/vendorStatus';
import { compactTableStyles } from '../common/tableStyles';
import { filterInputSx, dropdownChipSx } from '../common/filterStyles';


const VendorChangeTickets: React.FC = () => {
  const { search } = useLocation();
  const navigate = useNavigate();
  const searchParams = new URLSearchParams(search);

  // Read from URL
  const selectedStatus = (searchParams.get('status') as TVendorStatus) || 'pending';
  const docReviewedParam = searchParams.get('doc_reviewed'); // 'true' | 'false' | null (all)
  const organization = searchParams.get('organization') || '';
  const documentFilter = searchParams.get('document') || '';
  const extractionBatch = searchParams.get('extraction_batch') || '';
  const dataPool = searchParams.get('data_pool') || '';
  const vendorNameSearch = searchParams.get('vendor_name') || '';
  const page = Number(searchParams.get('page') || 0);
  const rowsPerPage = Number(searchParams.get('limit') || 10);

  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [docReviewedAnchorEl, setDocReviewedAnchorEl] = useState<null | HTMLElement>(null);
  const [orgInputValue, setOrgInputValue] = useState(organization);
  const [expandedRows, setExpandedRows] = useState<Set<number>>(new Set());
  const open = Boolean(anchorEl);
  const docReviewedMenuOpen = Boolean(docReviewedAnchorEl);

  // Local state for text filters (for debouncing)
  const [localDocumentFilter, setLocalDocumentFilter] = useState(documentFilter);
  const [localExtractionBatch, setLocalExtractionBatch] = useState(extractionBatch);
  const [localDataPool, setLocalDataPool] = useState(dataPool);
  const [localVendorNameSearch, setLocalVendorNameSearch] = useState(vendorNameSearch);

  // Debounce timer ref
  const debounceTimerRef = useRef<NodeJS.Timeout | null>(null);
  const DEBOUNCE_DELAY = 500;

  // Sync local state when URL params change externally (e.g., clear filters button)
  useEffect(() => {
    setLocalDocumentFilter(documentFilter);
    setLocalExtractionBatch(extractionBatch);
    setLocalDataPool(dataPool);
    setLocalVendorNameSearch(vendorNameSearch);
  }, [documentFilter, extractionBatch, dataPool, vendorNameSearch]);

  // Debounced URL update function
  const debouncedUpdateUrlParams = useCallback(
    (updates: Record<string, string | null>) => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
      debounceTimerRef.current = setTimeout(() => {
        updateUrlParams(updates);
      }, DEBOUNCE_DELAY);
    },
    [searchParams, navigate]
  );

  const toggleRowExpanded = (ticketId: number) => {
    setExpandedRows((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(ticketId)) {
        newSet.delete(ticketId);
      } else {
        newSet.add(ticketId);
      }
      return newSet;
    });
  };

  const offset = page * rowsPerPage;

  // Fetch organizations for autocomplete
  const { data: orgsData, loading: orgsLoading } = usePaginatedOrgs(orgInputValue, 50);
  const orgOptions: IOrg[] = orgsData?.results || [];

  const updateUrlParams = (updates: Record<string, string | null>) => {
    const params = new URLSearchParams(searchParams.toString());
    // Reset pagination on filter change (unless we're updating page/limit)
    if (!('page' in updates) && !('limit' in updates)) {
      params.set('page', '0');
    }
    Object.entries(updates).forEach(([key, value]) => {
      if (value !== null && value !== '') {
        params.set(key, value);
      } else {
        params.delete(key);
      }
    });
    navigate({ search: params.toString() }, { replace: true });
  };

  // Convert doc_reviewed param to boolean or undefined
  const documentReviewedValue =
    docReviewedParam === 'true' ? true : docReviewedParam === 'false' ? false : undefined;

  const {
    data: changeTicketsResponse,
    isLoading,
    isError,
  } = useGetChangeTickets({
    status: selectedStatus,
    limit: rowsPerPage,
    offset,
    documentReviewed: documentReviewedValue,
    organization: organization || undefined,
    document: documentFilter || undefined,
    extractionBatch: extractionBatch || undefined,
    dataPool: dataPool || undefined,
    normalizedVendorName: vendorNameSearch || undefined,
  });

  const changeTickets = changeTicketsResponse?.results || [];
  const totalCount = changeTicketsResponse?.count || 0;

  const handleStatusChange = (status: TVendorStatus) => {
    updateUrlParams({ status: status === 'pending' ? null : status });
    setAnchorEl(null);
  };

  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleChangePage = (event: unknown, newPage: number) => {
    updateUrlParams({ page: String(newPage) });
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    updateUrlParams({ limit: event.target.value, page: '0' });
  };

  const getStatusLabel = (status: TVendorStatus) => {
    return VENDOR_STATUS_CONFIG[status]?.label || VENDOR_STATUS_CONFIG.pending.label;
  };

  const getDocReviewedLabel = (value: string | null) => {
    if (value === 'true') return DOC_REVIEWED_OPTIONS.true;
    if (value === 'false') return DOC_REVIEWED_OPTIONS.false;
    return DOC_REVIEWED_OPTIONS.all;
  };

  const handleDocReviewedClick = (event: React.MouseEvent<HTMLElement>) => {
    setDocReviewedAnchorEl(event.currentTarget);
  };

  const handleDocReviewedClose = () => {
    setDocReviewedAnchorEl(null);
  };

  const handleDocReviewedChange = (value: string | null) => {
    updateUrlParams({ doc_reviewed: value });
    setDocReviewedAnchorEl(null);
  };

  const hasActiveFilters =
    organization ||
    documentFilter ||
    extractionBatch ||
    dataPool ||
    vendorNameSearch ||
    selectedStatus !== 'pending' ||
    docReviewedParam !== null;

  const handleClearFilters = () => {
    setOrgInputValue('');
    navigate({ search: '' }, { replace: true });
  };

  const handleOrgChange = (_event: React.SyntheticEvent, newValue: IOrg | string | null) => {
    if (newValue === null) {
      updateUrlParams({ organization: null });
    } else if (typeof newValue === 'string') {
      updateUrlParams({ organization: newValue || null });
    } else {
      updateUrlParams({ organization: newValue.id || null });
    }
  };

  // Find the selected org object for the autocomplete value
  const selectedOrg = organization ? orgOptions.find((o) => o.id === organization) || null : null;

  return (
    <>
      {/* Title row */}
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1.5 }}>
        <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1 }}>
          <Typography variant="h6">Vendor Change Tickets</Typography>
          {totalCount > 0 && (
            <Typography variant="body2" sx={{ color: '#5f6d7e' }}>
              {totalCount.toLocaleString()} results
            </Typography>
          )}
        </Box>
        {hasActiveFilters && (
          <Typography
            variant="body2"
            onClick={handleClearFilters}
            data-cy="btn-clear-filters"
            sx={{
              color: '#1D9AAA',
              cursor: 'pointer',
              fontSize: '12.5px',
              '&:hover': { textDecoration: 'underline' },
            }}
          >
            Clear filters
          </Typography>
        )}
      </Box>

      {/* Filter bar */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          mb: 1.5,
          flexWrap: 'wrap',
        }}
      >
        <Box sx={{ flex: 1 }} />

        {/* Status dropdown */}
        <Chip
          label={`Ticket Status: ${getStatusLabel(selectedStatus)}`}
          onClick={handleClick}
          deleteIcon={<KeyboardArrowDown sx={{ fontSize: '16px !important' }} />}
          onDelete={handleClick}
          data-cy="btn-status-filter"
          sx={{
            ...dropdownChipSx,
            ...(selectedStatus !== 'pending' && {
              borderColor: '#1D9AAA',
              backgroundColor: 'rgba(29,154,170,0.06)',
            }),
          }}
        />
        <Menu
          anchorEl={anchorEl}
          open={open}
          onClose={handleClose}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
          transformOrigin={{ vertical: 'top', horizontal: 'left' }}
          slotProps={{ paper: { sx: { mt: 0.5, borderRadius: '8px', minWidth: 120 } } }}
        >
          <MenuItem onClick={() => handleStatusChange('pending')} selected={selectedStatus === 'pending'}>
            Pending
          </MenuItem>
          <MenuItem onClick={() => handleStatusChange('applied')} selected={selectedStatus === 'applied'}>
            Applied
          </MenuItem>
          <MenuItem onClick={() => handleStatusChange('rejected')} selected={selectedStatus === 'rejected'}>
            Rejected
          </MenuItem>
        </Menu>

        {/* Reviewed dropdown */}
        <Chip
          label={`Source Data Status: ${getDocReviewedLabel(docReviewedParam)}`}
          onClick={handleDocReviewedClick}
          deleteIcon={<KeyboardArrowDown sx={{ fontSize: '16px !important' }} />}
          onDelete={handleDocReviewedClick}
          data-cy="btn-doc-reviewed-filter"
          sx={{
            ...dropdownChipSx,
            ...(docReviewedParam !== null && {
              borderColor: '#1D9AAA',
              backgroundColor: 'rgba(29,154,170,0.06)',
            }),
          }}
        />
        <Menu
          anchorEl={docReviewedAnchorEl}
          open={docReviewedMenuOpen}
          onClose={handleDocReviewedClose}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
          transformOrigin={{ vertical: 'top', horizontal: 'left' }}
          slotProps={{ paper: { sx: { mt: 0.5, borderRadius: '8px', minWidth: 120 } } }}
        >
          <MenuItem onClick={() => handleDocReviewedChange(null)} selected={docReviewedParam === null}>
            All
          </MenuItem>
          <MenuItem onClick={() => handleDocReviewedChange('true')} selected={docReviewedParam === 'true'}>
            Reviewed
          </MenuItem>
          <MenuItem onClick={() => handleDocReviewedChange('false')} selected={docReviewedParam === 'false'}>
            Not Reviewed
          </MenuItem>
        </Menu>

        <Box sx={{ width: '1px', height: 20, bgcolor: 'rgba(0,0,0,0.1)', mx: 0.25 }} />

        {/* Organization autocomplete */}
        <Autocomplete
          size="small"
          options={orgOptions}
          value={selectedOrg}
          onChange={handleOrgChange}
          inputValue={orgInputValue}
          onInputChange={(_event, newInputValue) => {
            setOrgInputValue(newInputValue);
          }}
          getOptionLabel={(option) => (typeof option === 'string' ? option : option.label)}
          isOptionEqualToValue={(option, value) => option.id === value.id}
          loading={orgsLoading}
          data-cy="filter-organization"
          sx={{ width: 160, ...filterInputSx }}
          renderInput={(params) => (
            <TextField
              {...params}
              size="small"
              label="Organization"
              variant="outlined"
              InputProps={{
                ...params.InputProps,
                endAdornment: (
                  <>
                    {orgsLoading ? <CircularProgress color="inherit" size={14} /> : null}
                    {params.InputProps.endAdornment}
                  </>
                ),
              }}
            />
          )}
        />

        {/* Document filter */}
        <TextField
          size="small"
          label="Document"
          variant="outlined"
          value={localDocumentFilter}
          onChange={(e) => {
            setLocalDocumentFilter(e.target.value);
            debouncedUpdateUrlParams({ document: e.target.value || null });
          }}
          data-cy="filter-document"
          sx={{ width: 120, ...filterInputSx }}
          InputProps={{
            endAdornment: localDocumentFilter && (
              <InputAdornment position="end">
                <IconButton
                  size="small"
                  onClick={() => {
                    setLocalDocumentFilter('');
                    updateUrlParams({ document: null });
                  }}
                  edge="end"
                  sx={{ mr: -1, p: 0.25 }}
                >
                  <Clear sx={{ fontSize: 14 }} />
                </IconButton>
              </InputAdornment>
            ),
          }}
        />

        {/* Batch filter */}
        <TextField
          size="small"
          label="Batch"
          variant="outlined"
          value={localExtractionBatch}
          onChange={(e) => {
            setLocalExtractionBatch(e.target.value);
            debouncedUpdateUrlParams({ extraction_batch: e.target.value || null });
          }}
          data-cy="filter-extraction-batch"
          sx={{ width: 100, ...filterInputSx }}
          InputProps={{
            endAdornment: localExtractionBatch && (
              <InputAdornment position="end">
                <IconButton
                  size="small"
                  onClick={() => {
                    setLocalExtractionBatch('');
                    updateUrlParams({ extraction_batch: null });
                  }}
                  edge="end"
                  sx={{ mr: -1, p: 0.25 }}
                >
                  <Clear sx={{ fontSize: 14 }} />
                </IconButton>
              </InputAdornment>
            ),
          }}
        />

        {/* Data Pool filter */}
        <TextField
          size="small"
          label="Data Pool"
          variant="outlined"
          value={localDataPool}
          onChange={(e) => {
            setLocalDataPool(e.target.value);
            debouncedUpdateUrlParams({ data_pool: e.target.value || null });
          }}
          data-cy="filter-data-pool"
          sx={{ width: 110, ...filterInputSx }}
          InputProps={{
            endAdornment: localDataPool && (
              <InputAdornment position="end">
                <IconButton
                  size="small"
                  onClick={() => {
                    setLocalDataPool('');
                    updateUrlParams({ data_pool: null });
                  }}
                  edge="end"
                  sx={{ mr: -1, p: 0.25 }}
                >
                  <Clear sx={{ fontSize: 14 }} />
                </IconButton>
              </InputAdornment>
            ),
          }}
        />

        {/* Vendor Name filter */}
        <TextField
          size="small"
          label="Vendor Name"
          variant="outlined"
          value={localVendorNameSearch}
          onChange={(e) => {
            setLocalVendorNameSearch(e.target.value);
            debouncedUpdateUrlParams({ vendor_name: e.target.value || null });
          }}
          data-cy="filter-vendor-name"
          sx={{ width: 140, ...filterInputSx }}
          InputProps={{
            endAdornment: localVendorNameSearch && (
              <InputAdornment position="end">
                <IconButton
                  size="small"
                  onClick={() => {
                    setLocalVendorNameSearch('');
                    updateUrlParams({ vendor_name: null });
                  }}
                  edge="end"
                  sx={{ mr: -1, p: 0.25 }}
                >
                  <Clear sx={{ fontSize: 14 }} />
                </IconButton>
              </InputAdornment>
            ),
          }}
        />
      </Box>
      {isError ? (
        <div>Error loading change tickets.</div>
      ) : (
        <Box id="vendor-change-tickets-list" sx={compactTableStyles.containerSx}>
          <Table sx={compactTableStyles.tableSx}>
            <TableHead sx={compactTableStyles.tableHeadSx}>
              <TableRow>
                <TableCell sx={{ width: 32, p: '0.5 !important' }} />
                <TableCell align="left">Discovered Normalized Vendor Name</TableCell>
                <TableCell align="left" sx={{ minWidth: 280 }}>Set Standard Vendor Name</TableCell>
                <TableCell align="left">Source Batch</TableCell>
                <TableCell align="center">Occurrences</TableCell>
                <TableCell align="center">Status</TableCell>
                <TableCell align="center">Accept/Reject</TableCell>
              </TableRow>
            </TableHead>
            <TableBody
              data-cy="tablebody-vendor-change-tickets"
              sx={[
                compactTableStyles.tableBodySx,
                {
                  // Override to direct child selector to avoid styling nested tables in expanded rows
                  '& .MuiTableRow-root': {},
                  '& > .MuiTableRow-root': {
                    borderBottom: '1px solid rgba(0,0,0,0.04)',
                    transition: 'background 0.1s',
                    '&:hover': { bgcolor: '#F1F6F8' },
                    '&:last-child': { borderBottom: 'none' },
                  },
                },
              ]}
            >
              {isLoading ? (
                Array.from({ length: 5 }).map((_, i) => (
                  <TableRow key={i}>
                    <TableCell sx={{ width: 32, p: '0.5 !important' }} />
                    <TableCell><Skeleton animation="wave" variant="rounded" height={14} width={`${40 + (i * 21) % 45}%`} sx={{ borderRadius: '4px' }} /></TableCell>
                    <TableCell><Skeleton animation="wave" variant="rounded" height={14} width={`${35 + (i * 17) % 50}%`} sx={{ borderRadius: '4px' }} /></TableCell>
                    <TableCell><Skeleton animation="wave" variant="rounded" height={14} width={80} sx={{ borderRadius: '4px' }} /></TableCell>
                    <TableCell align="center"><Skeleton animation="wave" variant="rounded" height={14} width={24} sx={{ borderRadius: '4px', mx: 'auto' }} /></TableCell>
                    <TableCell align="center"><Skeleton animation="wave" variant="rounded" height={22} width={56} sx={{ borderRadius: '6px', mx: 'auto' }} /></TableCell>
                    <TableCell align="right"><Skeleton animation="wave" variant="rounded" height={14} width={48} sx={{ borderRadius: '4px', ml: 'auto' }} /></TableCell>
                  </TableRow>
                ))
              ) : changeTickets?.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7} align="center" sx={{ padding: 3 }}>
                    No change tickets found
                  </TableCell>
                </TableRow>
              ) : (
                changeTickets?.map((ticket: VendorChangeTicketEntry) => (
                  <React.Fragment key={ticket.id}>
                    <TableRow>
                      <TableCell sx={{ width: 32, p: '0.5 !important' }} align="center">
                        {ticket.occurrences.length > 1 && (
                          <IconButton
                            size="small"
                            onClick={() => toggleRowExpanded(ticket.id)}
                            sx={{ p: 0 }}
                            data-cy={`expand-occurrences-${ticket.id}`}
                          >
                            {expandedRows.has(ticket.id) ? (
                              <KeyboardArrowDown fontSize="small" />
                            ) : (
                              <KeyboardArrowRight fontSize="small" />
                            )}
                          </IconButton>
                        )}
                      </TableCell>
                      <TableCell
                        align="left"
                        data-cy={`tablecell-vendor-change-tickets-vendor-${ticket.normalized_vendor_name}`}
                      >
                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                          {ticket.normalized_vendor_name}
                          <Tooltip title="Filter by vendor name" placement="top">
                            <FilterAlt
                              sx={{
                                fontSize: 14,
                                ml: 0.5,
                                color: '#5f6d7e',
                                cursor: 'pointer',
                                '&:hover': { color: '#1D9AAA' },
                              }}
                              onClick={() =>
                                updateUrlParams({ vendor_name: ticket.normalized_vendor_name })
                              }
                            />
                          </Tooltip>
                        </Box>
                      </TableCell>
                      <TableCell align="left">
                        <StandardVendorNameCell ticket={ticket} />
                      </TableCell>
                      <TableCell align="left">
                        {ticket.occurrences.length > 0 ? (
                          <Box sx={{ display: 'flex', alignItems: 'center' }}>
                            <LinkTableStyled
                              to={`/data-pools/${ticket.occurrences[0].data_pool}/extraction-batch/${ticket.occurrences[0].extraction_batch}/review/${ticket.occurrences[0].extraction}`}
                              target="_blank"
                            >
                              {ticket.occurrences[0].extraction_batch}
                            </LinkTableStyled>
                            <Tooltip title="Filter by batch" placement="top">
                              <FilterAlt
                                sx={{
                                  fontSize: 14,
                                  ml: 0.5,
                                  color: '#5f6d7e',
                                  cursor: 'pointer',
                                  '&:hover': { color: '#1D9AAA' },
                                }}
                                onClick={() =>
                                  updateUrlParams({
                                    extraction_batch: ticket.occurrences[0].extraction_batch,
                                  })
                                }
                              />
                            </Tooltip>
                            {ticket.occurrences.length > 1 && (
                              <Chip
                                label={`+${ticket.occurrences.length - 1}`}
                                size="small"
                                onClick={() => toggleRowExpanded(ticket.id)}
                                sx={{
                                  ml: 0.5,
                                  height: 20,
                                  fontSize: '0.7rem',
                                  cursor: 'pointer',
                                }}
                              />
                            )}
                          </Box>
                        ) : (
                          '-'
                        )}
                      </TableCell>
                      <TableCell align="center" sx={{ color: '#5f6d7e' }}>
                        {ticket.count || 0}
                      </TableCell>
                      <TableCell align="center">
                        <VendorStatusBadge status={ticket.status || 'pending'} />
                      </TableCell>
                      <TableCell align="right">
                        <TicketActionsCell ticket={ticket} />
                      </TableCell>
                    </TableRow>
                    {expandedRows.has(ticket.id) && ticket.occurrences.length > 1 && (
                      <TableRow sx={{ '&:hover': { bgcolor: 'transparent !important' } }}>
                        <TableCell
                          colSpan={7}
                          sx={{
                            py: 1,
                            px: 2,
                            backgroundColor: '#f9fafb',
                            borderBottom: '1px solid rgba(0,0,0,0.08) !important',
                          }}
                        >
                          <Box sx={{ pl: 4 }}>
                            <Typography
                              variant="caption"
                              sx={{ fontWeight: 600, color: 'text.secondary', mb: 1, display: 'block' }}
                            >
                              All Occurrences ({ticket.occurrences.length})
                            </Typography>
                            <Table size="small" sx={{ backgroundColor: 'white' }}>
                              <TableHead>
                                <TableRow>
                                  <TableCell sx={{ fontWeight: 600, fontSize: '0.75rem', py: 0.5 }}>
                                    Organization
                                  </TableCell>
                                  <TableCell sx={{ fontWeight: 600, fontSize: '0.75rem', py: 0.5 }}>
                                    Data Pool
                                  </TableCell>
                                  <TableCell sx={{ fontWeight: 600, fontSize: '0.75rem', py: 0.5 }}>
                                    Document
                                  </TableCell>
                                  <TableCell sx={{ fontWeight: 600, fontSize: '0.75rem', py: 0.5 }}>
                                    Extraction Batch
                                  </TableCell>
                                </TableRow>
                              </TableHead>
                              <TableBody>
                                {ticket.occurrences.map((occ, idx) => (
                                  <TableRow key={idx}>
                                    <TableCell sx={{ fontSize: '0.8rem', py: 0.5 }}>
                                      {occ.organization || '-'}
                                    </TableCell>
                                    <TableCell sx={{ fontSize: '0.8rem', py: 0.5 }}>
                                      {occ.data_pool || '-'}
                                    </TableCell>
                                    <TableCell sx={{ fontSize: '0.8rem', py: 0.5 }}>
                                      {occ.document ? (
                                        <LinkTableStyled
                                          to={`/data-pools/${occ.data_pool}/documents/${occ.document}`}
                                          target="_blank"
                                        >
                                          {occ.document}
                                        </LinkTableStyled>
                                      ) : (
                                        '-'
                                      )}
                                    </TableCell>
                                    <TableCell sx={{ fontSize: '0.8rem', py: 0.5 }}>
                                      {occ.extraction_batch ? (
                                        <LinkTableStyled
                                          to={`/data-pools/${occ.data_pool}/extraction-batch/${occ.extraction_batch}/review/${occ.extraction}`}
                                          target="_blank"
                                        >
                                          {occ.extraction_batch}
                                        </LinkTableStyled>
                                      ) : (
                                        '-'
                                      )}
                                    </TableCell>
                                  </TableRow>
                                ))}
                              </TableBody>
                            </Table>
                          </Box>
                        </TableCell>
                      </TableRow>
                    )}
                  </React.Fragment>
                ))
              )}
            </TableBody>
          </Table>
          {totalCount > 0 && <TablePagination
            rowsPerPageOptions={[10, 25, 50, 100]}
            component="div"
            count={totalCount}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
            backIconButtonProps={{
              'aria-label': 'Previous Page',
            }}
            nextIconButtonProps={{
              'aria-label': 'Next Page',
            }}
          />}
        </Box>
      )}
    </>
  );
};

export default VendorChangeTickets;
